import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import {
  apiClient,
  type Resource,
  type MergeCandidateGroup,
  type LinkedConnection,
} from '@/lib/api-client';
import {
  Edit,
  FileText,
  Filter,
  GitMerge,
  Info,
  Link2,
  Loader2,
  Plus,
  Shield,
  Trash2,
  Users,
  Wrench,
} from 'lucide-react';
import { MergeResourcesDialog } from '@/components/resources/MergeResourcesDialog';
import { useFeatureFlags } from '@/lib/feature-flags';

function TypeBadge({ type }: { type?: string }) {
  const t = (type || 'mcp').toLowerCase();
  const colors =
    t === 'mcp'
      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      : t === 'a2a'
      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      : t === 'api'
      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${colors}`}>
      {t}
    </span>
  );
}

function EmptyBadge() {
  // ``url=""`` masters survive backfill from migration 004 — they look
  // duplicate-mergeable in the UI but each carries a distinct
  // connection. Badge them so admins don't pick them as a merge target,
  // and so they're easy to find when configuring a URL.
  return (
    <span
      title="No URL set — this master is not yet routable. Open the linked agent to configure it."
      className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
    >
      Empty
    </span>
  );
}

interface ToolCounts {
  total: number;
  unclassified: number;
}

export const ResourcesPage = () => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [candidateGroups, setCandidateGroups] = useState<MergeCandidateGroup[]>([]);
  const [toolCounts, setToolCounts] = useState<Record<string, ToolCounts>>({});
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [deletingResource, setDeletingResource] = useState<Resource | null>(null);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [mergeSource, setMergeSource] = useState<string | null>(null);
  const [linkedDetailFor, setLinkedDetailFor] = useState<string | null>(null);
  const [linkedDetailRows, setLinkedDetailRows] = useState<LinkedConnection[]>([]);
  const [linkedDetailLoading, setLinkedDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { publicConfig, isEnabled } = useFeatureFlags();
  const gatewayProtocolVersion = publicConfig?.mcp_protocol_version || '';
  const [formData, setFormData] = useState({
    name: '',
    type: 'mcp',
    url: '',
    description: '',
    tags: '',
    enabled: true,
    protocol_version: '',
  });
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [list, candidates] = await Promise.all([
        apiClient.listResources(),
        apiClient.listMergeCandidates().catch(() => [] as MergeCandidateGroup[]),
      ]);
      setResources(Array.isArray(list) ? list : []);
      setCandidateGroups(candidates);

      try {
        const countsResp = await apiClient.listResourceToolCounts();
        const counts: Record<string, ToolCounts> = {};
        for (const r of countsResp.resources) {
          counts[r.resource_name] = {
            total: r.total,
            unclassified: r.unclassified,
          };
        }
        setToolCounts(counts);
      } catch {
        // Tools inventory not yet initialized — leave counts empty.
      }
    } catch (error: any) {
      if (error.status !== 401) {
        setResources([]);
      }
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to load resources',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const openLinkedDetail = async (name: string) => {
    setLinkedDetailFor(name);
    setLinkedDetailLoading(true);
    try {
      const detail = await apiClient.getResource(name);
      setLinkedDetailRows(detail.linked_connections || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to load linked connections',
        variant: 'destructive',
      });
      setLinkedDetailRows([]);
    } finally {
      setLinkedDetailLoading(false);
    }
  };

  const closeLinkedDetail = () => {
    setLinkedDetailFor(null);
    setLinkedDetailRows([]);
  };

  const openCreate = () => {
    setFormData({
      name: '',
      type: 'mcp',
      url: '',
      description: '',
      tags: '',
      enabled: true,
      protocol_version: '',
    });
    setCreateDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Validation Error', description: 'Name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await apiClient.createResource({
        name: formData.name.trim(),
        type: formData.type,
        url: formData.url.trim(),
        description: formData.description.trim(),
        tags: formData.tags
          ? formData.tags.split(',').map((t) => t.trim()).filter(Boolean)
          : [],
        enabled: formData.enabled,
      });
      toast({ title: 'Created', description: `Resource '${formData.name}' created` });
      setCreateDialogOpen(false);
      loadData();
    } catch (error: any) {
      toast({
        title: error.status === 409 ? 'Already exists' : 'Error',
        description: error.message || 'Failed to create resource',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (resource: Resource) => {
    setEditingResource(resource);
    setFormData({
      name: resource.name,
      type: resource.type || resource.protocol || 'mcp',
      url: resource.url || resource.mcp_url || '',
      description: resource.description || '',
      tags: (resource.tags || []).join(', '),
      enabled: resource.enabled !== false,
      protocol_version: resource.config?.protocol_version ?? '',
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingResource) return;
    if (!formData.url.trim()) {
      toast({ title: 'Validation Error', description: 'URL is required', variant: 'destructive' });
      return;
    }

    const slugRegex = /^[a-z][a-z0-9-]*[a-z0-9]$|^[a-z]$/;
    if (!slugRegex.test(formData.name.trim())) {
      toast({ title: "Validation Error", description: "Name must be a lowercase slug (a-z, 0-9, hyphens — no spaces or uppercase)", variant: "destructive" });
      return;
    }


    setSaving(true);
    try {
      const updatedConfig = { ...(editingResource?.config || {}) };
      if (formData.protocol_version.trim()) {
        updatedConfig.protocol_version = formData.protocol_version.trim();
      } else {
        delete updatedConfig.protocol_version;
      }
      const payload: Partial<Resource> = {
        name: formData.name.trim(),
        type: formData.type,
        url: formData.url.trim(),
        description: formData.description.trim(),
        tags: formData.tags
          ? formData.tags.split(',').map((t) => t.trim()).filter(Boolean)
          : [],
        enabled: formData.enabled,
        config: updatedConfig,
      };
      await apiClient.updateResource(editingResource.name, payload);
      toast({ title: 'Success', description: 'Resource updated' });
      setEditDialogOpen(false);
      loadData();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save resource',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (force = false) => {
    if (!deletingResource) return;
    try {
      await apiClient.deleteResource(deletingResource.name, { force });
      toast({
        title: 'Deleted',
        description: force
          ? `Resource '${deletingResource.name}' deleted (${deletingResource.linked_connection_count || 0} connection(s) detached)`
          : `Resource '${deletingResource.name}' deleted`,
      });
      setDeleteDialogOpen(false);
      setDeletingResource(null);
      loadData();
    } catch (error: any) {
      toast({
        title: error.status === 409 ? 'Linked connections' : 'Error',
        description: error.message || 'Failed to delete resource',
        variant: 'destructive',
      });
    }
  };

  const handleMergeOpen = (sourceName: string) => {
    setMergeSource(sourceName);
    setMergeDialogOpen(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Resources</h2>
          <p className="text-muted-foreground">
            One row per logical MCP target. Multiple agent connections can share a master — use Merge to consolidate duplicates.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" /> Create resource
        </Button>
      </div>

      {/* Suggested merges — masters that share a connection_resource_id */}
      {candidateGroups.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/40 dark:bg-amber-950/20">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <GitMerge className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-semibold">Suggested merges</h3>
              <span className="text-xs text-muted-foreground">
                {candidateGroups.length} group{candidateGroups.length === 1 ? '' : 's'}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              These masters share an Okta-side identifier (auth server, vault secret, service account). They likely point at the same backend — merging consolidates tool inventory and Cedar policies onto one slug.
            </p>
            <div className="space-y-2">
              {candidateGroups.map((group) => (
                <div
                  key={group.connection_resource_id}
                  className="rounded border bg-background p-3 space-y-2"
                >
                  <div className="text-xs font-mono text-muted-foreground truncate">
                    {group.connection_resource_id}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {group.candidates.map((c, idx) => (
                      <span key={c.resource_id} className="inline-flex items-center gap-1">
                        <Link
                          to={`/dashboard/resources/${encodeURIComponent(c.resource_name)}`}
                          className="font-mono text-xs hover:underline"
                        >
                          {c.resource_name}
                        </Link>
                        {!c.url && <EmptyBadge />}
                        {idx < group.candidates.length - 1 && (
                          <span className="text-muted-foreground">·</span>
                        )}
                      </span>
                    ))}
                  </div>
                  <div className="pt-1 flex flex-wrap gap-2">
                    {group.candidates
                      .filter((c) => !!c.url)
                      .map((c) => (
                        <Button
                          key={c.resource_id}
                          variant="outline"
                          size="sm"
                          onClick={() => handleMergeOpen(c.resource_name)}
                          className="h-7 text-xs"
                        >
                          <GitMerge className="w-3 h-3 mr-1" />
                          Merge into {c.resource_name}…
                        </Button>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {resources.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Name</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Type</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">URL</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Connections</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Tools</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Enabled</th>
                    <th className="text-right p-3 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {resources.map((r) => {
                    const counts = toolCounts[r.name];
                    const url = r.url || r.mcp_url || '';
                    const linkedCount = r.linked_connection_count ?? 0;
                    const isEmpty = !url;
                    return (
                      <tr key={r.name} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="p-3 text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/dashboard/resources/${encodeURIComponent(r.name)}`}
                              className="hover:underline"
                            >
                              {r.name}
                            </Link>
                            {isEmpty && <EmptyBadge />}
                          </div>
                        </td>
                        <td className="p-3">
                          <TypeBadge type={r.type || r.protocol} />
                        </td>
                        <td className="p-3 text-sm max-w-xs truncate">
                          {url || <span className="text-muted-foreground italic">not set</span>}
                        </td>
                        <td className="p-3 text-sm">
                          <button
                            type="button"
                            onClick={() => openLinkedDetail(r.name)}
                            className="inline-flex items-center gap-1 hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:text-muted-foreground"
                            disabled={linkedCount === 0}
                          >
                            <Users className="w-3 h-3 text-muted-foreground" />
                            <span className="tabular-nums">{linkedCount}</span>
                          </button>
                        </td>
                        <td className="p-3 text-sm">
                          {counts ? (
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/dashboard/resources/${encodeURIComponent(r.name)}`}
                                className="inline-flex items-center gap-1 hover:underline"
                              >
                                <Wrench className="w-3 h-3 text-muted-foreground" />
                                <span className="tabular-nums">{counts.total}</span>
                              </Link>
                              {counts.unclassified > 0 && (
                                <span
                                  title={`${counts.unclassified} unclassified`}
                                  className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
                                >
                                  {counts.unclassified} unclassified
                                </span>
                              )}
                              {r.tool_filtering && (
                                <Link
                                  to={`/dashboard/resources/${encodeURIComponent(r.name)}?tab=tool-access`}
                                  title="Tool filtering is on: each agent can only use its allowed tools"
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-primary/10 text-primary hover:bg-primary/15"
                                >
                                  <Filter className="w-3 h-3" />
                                  Filtered
                                </Link>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="p-3">
                          <span
                            className={`inline-block w-2 h-2 rounded-full ${r.enabled !== false ? 'bg-green-500' : 'bg-gray-400'}`}
                          />
                          <span className="ml-1.5 text-sm">{r.enabled !== false ? 'Yes' : 'No'}</span>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Link to={`/dashboard/resources/${encodeURIComponent(r.name)}/audit`}>
                              <Button variant="ghost" size="icon" title="View Audit">
                                <FileText className="w-4 h-4" />
                              </Button>
                            </Link>
                            {isEnabled('cedarEnabled') && (
                              <Button
                                variant="ghost"
                                size="icon"
                                title="View Policies"
                                onClick={() => navigate('/dashboard/policies?resource=' + encodeURIComponent(r.name))}
                              >
                                <Shield className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(r)}
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleMergeOpen(r.name)}
                              title="Merge into another resource"
                              disabled={isEmpty}
                            >
                              <GitMerge className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setDeletingResource(r);
                                setDeleteDialogOpen(true);
                              }}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mb-4" />
            <p className="text-lg font-medium text-muted-foreground mb-2">No resources yet</p>
            <p className="text-sm text-muted-foreground text-center max-w-lg">
              Create a master resource to route requests, or open an{' '}
              <Link to="/dashboard/agents" className="underline">agent</Link>{' '}
              and configure one of its Okta Managed Connections.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/30 border">
        <Info className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
        <p className="text-sm text-muted-foreground">
          Each row is a master resource. Connection-level state (auth method, scopes, type) lives
          on each linked agent_connection — open the master detail page to see them. Merging
          consolidates tool inventory and Cedar policies onto a single slug.
        </p>
      </div>

      {/* Linked-connections detail modal */}
      <Dialog open={!!linkedDetailFor} onOpenChange={(open) => !open && closeLinkedDetail()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Linked connections — {linkedDetailFor}</DialogTitle>
            <DialogDescription>
              Agents and Okta Managed Connections currently routed to this master.
            </DialogDescription>
          </DialogHeader>
          {linkedDetailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : linkedDetailRows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">
              No linked connections.
            </p>
          ) : (
            <div className="space-y-2">
              {linkedDetailRows.map((c) => (
                <div
                  key={`${c.agent_id}:${c.connection_id}`}
                  className="rounded border p-3 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <Link2 className="w-3 h-3 text-muted-foreground" />
                    <Link
                      to={`/dashboard/agents/${encodeURIComponent(c.agent_id)}`}
                      className="font-medium hover:underline"
                    >
                      {c.agent_name || c.agent_id}
                    </Link>
                    <span className="text-xs text-muted-foreground font-mono">
                      {c.connection_id}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <span>type: {c.connection_type}</span>
                    {c.auth_method && <span>auth: {c.auth_method}</span>}
                    {c.scope_condition && <span>scopes: {c.scope_condition}</span>}
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        c.enabled
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {c.enabled ? 'enabled' : 'disabled'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeLinkedDetail}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="flex flex-col max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Create resource</DialogTitle>
            <DialogDescription>
              Standalone master resource. Link Okta Managed Connections to it from each agent's detail page.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1">
            <ResourceForm formData={formData} setFormData={setFormData} mode="create" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={saving}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="flex flex-col max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Edit resource</DialogTitle>
            <DialogDescription>
              Update routing fields. Connection-level state (auth method, scopes) lives on each linked agent_connection.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1">
            <ResourceForm formData={formData} setFormData={setFormData} mode="edit" gatewayProtocolVersion={gatewayProtocolVersion} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={saving}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete resource</DialogTitle>
            <DialogDescription>
              {deletingResource && (deletingResource.linked_connection_count || 0) > 0
                ? `This resource is linked to ${deletingResource.linked_connection_count} agent connection(s). Force-deleting detaches them all — they keep their sync state but stop routing until linked to another master.`
                : "Are you sure you want to delete this resource?"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                handleDelete(
                  (deletingResource?.linked_connection_count || 0) > 0,
                )
              }
            >
              {(deletingResource?.linked_connection_count || 0) > 0
                ? 'Force delete'
                : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge dialog */}
      {mergeSource && (
        <MergeResourcesDialog
          source={mergeSource}
          open={mergeDialogOpen}
          onOpenChange={(open) => {
            setMergeDialogOpen(open);
            if (!open) setMergeSource(null);
          }}
          onMerged={() => {
            setMergeDialogOpen(false);
            setMergeSource(null);
            loadData();
          }}
        />
      )}
    </div>
  );
};

interface ResourceFormProps {
  formData: {
    name: string;
    type: string;
    url: string;
    description: string;
    tags: string;
    enabled: boolean;
    protocol_version: string;
  };
  setFormData: (next: ResourceFormProps['formData']) => void;
  mode: 'create' | 'edit';
  gatewayProtocolVersion?: string;
}

export function ResourceForm({ formData, setFormData, mode, gatewayProtocolVersion = '' }: ResourceFormProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="rf-name">Name *</Label>
        <Input
          id="rf-name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="hr-system"
          disabled={mode === 'edit'}
        />
        <p className="text-xs text-muted-foreground">
          Lowercase slug (a-z, 0-9, hyphens). Globally unique.
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="rf-type">Type</Label>
        <select
          id="rf-type"
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="mcp">MCP</option>
          <option value="api">API</option>
          <option value="a2a">A2A</option>
        </select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="rf-url">
          URL{mode === 'edit' && ' *'}
        </Label>
        <Input
          id="rf-url"
          value={formData.url}
          onChange={(e) => setFormData({ ...formData, url: e.target.value })}
          placeholder="https://hr-mcp.example.com/mcp"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="rf-description">Description</Label>
        <Textarea
          id="rf-description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="HR resource for employee lookups"
          rows={2}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="rf-protocol-version">MCP Protocol Version</Label>
        <Input
          id="rf-protocol-version"
          value={formData.protocol_version}
          onChange={(e) => setFormData({ ...formData, protocol_version: e.target.value })}
          placeholder={`leave blank to use gateway default${gatewayProtocolVersion ? ` (${gatewayProtocolVersion})` : ''}`}
        />
        <p className="text-xs text-muted-foreground">
          Override the MCP protocol version sent to this resource during initialize.
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="rf-tags">Tags</Label>
        <Input
          id="rf-tags"
          value={formData.tags}
          onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
          placeholder="hr, employees, internal (comma-separated)"
        />
      </div>
      <div className="flex items-center gap-2">
        <input
          id="rf-enabled"
          type="checkbox"
          checked={formData.enabled}
          onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
          className="h-4 w-4"
        />
        <Label htmlFor="rf-enabled">Enabled</Label>
      </div>
    </div>
  );
}
